import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCartItems(cart);
  };

  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    const updatedCart = [...cartItems];
    updatedCart[index].quantity = newQuantity;
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
  };

  const removeItem = (index) => {
    const updatedCart = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    toast.success('Item removed from cart');
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal > 500 ? 0 : 50;
  const total = subtotal + shipping;

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12 lg:py-16">
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-12">
          CART
        </h1>

        {cartItems.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-2xl font-bold tracking-wider text-gray-400 mb-8">
              YOUR CART IS EMPTY
            </p>
            <button
              onClick={() => navigate('/catalog')}
              className="bg-black text-white px-10 py-5 text-sm font-bold tracking-wider hover:bg-gray-800 transition-colors"
            >
              CONTINUE SHOPPING
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-8">
              {cartItems.map((item, index) => (
                <div key={index} className="flex gap-6 pb-8 border-b border-gray-200">
                  {/* Image */}
                  <div
                    className="w-32 h-40 flex-shrink-0"
                    style={{ backgroundColor: item.colorBlock }}
                  />

                  {/* Details */}
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-bold tracking-wider">{item.name}</h3>
                        <button
                          onClick={() => removeItem(index)}
                          className="hover:opacity-50 transition-opacity"
                        >
                          <X size={20} strokeWidth={2.5} />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">Size: {item.selectedSize}</p>
                      <p className="text-lg font-bold">${item.price}</p>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(index, item.quantity - 1)}
                        className="w-10 h-10 border-2 border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                      >
                        <Minus size={16} strokeWidth={2.5} />
                      </button>
                      <span className="text-sm font-bold w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(index, item.quantity + 1)}
                        className="w-10 h-10 border-2 border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                      >
                        <Plus size={16} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 p-8 sticky top-32">
                <h2 className="text-2xl font-black tracking-tighter mb-8">ORDER SUMMARY</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span className="font-bold">${subtotal}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Shipping</span>
                    <span className="font-bold">{shipping === 0 ? 'FREE' : `$${shipping}`}</span>
                  </div>
                  {subtotal < 500 && (
                    <p className="text-xs text-gray-600">
                      Free shipping on orders over $500
                    </p>
                  )}
                  <div className="border-t border-gray-300 pt-4 flex justify-between">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-xl">${total}</span>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full bg-black text-white py-5 text-sm font-bold tracking-wider hover:bg-gray-800 transition-colors mb-4"
                >
                  PROCEED TO CHECKOUT
                </button>

                <button
                  onClick={() => navigate('/catalog')}
                  className="w-full border-2 border-black py-5 text-sm font-bold tracking-wider hover:bg-black hover:text-white transition-colors"
                >
                  CONTINUE SHOPPING
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
